import { AibLineType, AsupInternalBlock } from "aic";
import { v3EditorProps } from "components/common/v3EditorProps";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { HierarchyContext } from "contexts/HierarchyContext";
import { getExternalSingles } from "contexts/tlf/getExternalSingles";
import { TlfContext } from "contexts/TlfContext";
import { ReactNode, useContext } from "react";
import { UPDATE_TLF } from "../../contexts/tlf/interface";
import { eHierarchyLevel } from "../../functions/api/enums";
import { CommentScope } from "../common/comments/CommentScope";

interface FootnotesProps {
  noComments?: boolean;
  patch?: boolean;
  canAddFirstRow?: boolean;
}

/**
 * Render the footnotes editor block for a TLF item, optionally patched to the server.
 *
 * @param props - Destructured properties for the footnotes component.
 * @param props.patch - When true, persists changes via the TLF patch API.
 * @param props.noComments - When true, disables comment functionality.
 * @param props.canAddFirstRow - When true, allows adding the first row to an empty block.
 * @returns A React node containing the footnotes editor or null when context missing.
 */
export const Footnotes = ({
  patch = false,
  noComments = false,
  canAddFirstRow = false,
}: FootnotesProps): ReactNode => {
  const appSettings = useContext(AppSettingsContext);
  const hierarchyContext = useContext(HierarchyContext);
  const tlfContext = useContext(TlfContext);
  const tlfData = tlfContext?.tlfData;
  const patchTlf = tlfContext?.patchTlf;

  const externalSingles = getExternalSingles(
    undefined,
    tlfData?.releaseStatus,
    tlfData?.domain,
    tlfData?.singles,
    hierarchyContext?.hierarchy?.level,
    hierarchyContext?.hierarchy?.singles,
    hierarchyContext?.hierarchy?.domainReplacements,
    hierarchyContext?.parentTemplates.singles,
    hierarchyContext?.parentTemplates.domainReplacements,
  );

  return !hierarchyContext || !tlfData ? null : (
    <CommentScope
      id="footnotes-comment"
      activeContext="TlfItem"
      commentLabel="New footnotes comment"
      disabled={noComments}
    >
      <AsupInternalBlock
        id="footnotes"
        canAddFirstRow={canAddFirstRow}
        lineStyle={{ ...appSettings.defaultTableStyle }}
        lines={tlfData.footnotes}
        disabled={tlfContext.readOnly}
        onChange={(ret) => {
          if (patch && patchTlf) {
            patchTlf("footnotes", ret);
          } else {
            tlfContext.dispatch!({
              operation: UPDATE_TLF,
              tlfData: { footnotes: ret },
              calledFrom: "Footnotes:Update",
            });
          }
        }}
        externalSingles={externalSingles}
        minLines={2}
        defaultType={AibLineType.leftOnly}
        editorProps={v3EditorProps}
        noComments={noComments}
        noTrafficLights={
          !tlfContext.showTrafficLights ||
          tlfData.primaryHierarchyLevel !== eHierarchyLevel.Report ||
          !("body" in tlfData) ||
          tlfData.coreTrafficLightSwitch === "DoNotCalculate"
        }
        changedBlocks={tlfData.changedFootnotes}
      />
    </CommentScope>
  );
};

Footnotes.displayName = "Footnotes";
